import { Component, Input, OnChanges, OnDestroy, OnInit, QueryList, SimpleChanges, ViewChildren, inject } from '@angular/core';
import { forkJoin, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { environment } from '../../../../environments/environment.defaults';
import {
  cityAndStateSortOptions,
  contractEndDateSortOptions,
  contractNameSortOptions,
  equipmentLocationSortOptions,
  functionalLocationSortOptions,
  modalitySortOptions,
  productNameSortOptions,
} from '../../../configurations/sort-options';
import { CatalogFacade } from '../../../facades/catalog.facade';
import { AdvancedFilter } from '../../../models/advanced-filter';
import { IModalitiesAndMaterialNumbers } from '../../../models/cpq.models';
import { SortByComponent } from '../../../shared/sort-by/sort-by.component';
import { DateUtils } from '../../../utils/date.utils';
import { ICart } from '../../../models/cart.models';
import { EUserRoles } from '../../../configurations/common';
import { FormatDatePipe } from '../../../shared/pipes/format-date.pipe';
import { EquipmentUtils } from '../../../utils/equipment.utils';
import { IEquipmentSelectionHeaders } from '../../../models/equipmentselection.models';
import { IShsEquipmentData } from '../../../models/installedbase.models';
import { ArrayUtils } from '../../../utils/array.utils';

@Component({
  selector: 'app-functional-locations-list',
  templateUrl: './functional-locations-list.component.html',
  styleUrls: ['./functional-locations-list.component.scss'],
  standalone: false,
})
export class FunctionalLocationsListComponent implements OnInit, OnChanges, OnDestroy {
  private catalogFacade: CatalogFacade = inject(CatalogFacade);
  private formatDatePipe: FormatDatePipe = inject(FormatDatePipe);

  tabsOpened: string[] = [];
  headers: IEquipmentSelectionHeaders[] = [
    {
      label: 'my-contracts.functional-location',
      class: 'functional-location-id',
      sortOptions: functionalLocationSortOptions,
      showOnMobile: true,
    },
    {
      label: 'my-contracts.modality',
      class: 'modality',
      sortOptions: modalitySortOptions,
      showOnMobile: false,
    },
    {
      label: 'my-contracts.product-name',
      class: 'nameEnUs',
      sortOptions: productNameSortOptions,
      showOnMobile: true,
    },
    {
      label: 'my-contracts.equipment-location',
      class: 'street',
      sortOptions: equipmentLocationSortOptions,
      showOnMobile: false,
    },
    {
      label: 'my-contracts.city-state',
      class: 'state',
      sortOptions: cityAndStateSortOptions,
      showOnMobile: false,
    },
    {
      label: 'my-contracts.contract-end',
      class: 'contractExpirationDate',
      sortOptions: contractEndDateSortOptions,
      showOnMobile: false,
    },
  ];

  myEquipmentAdditionalHeader: IEquipmentSelectionHeaders = {
    label: 'my-contracts.contract-name',
    class: 'contractName',
    sortOptions: contractNameSortOptions,
    showOnMobile: false,
  };

  searchExcludedAttributes: string[] = environment.searchExcludedAttributes;
  currentSort: Record<string, never> = {};
  isLoading: boolean = true;
  agreementsToWorkWith!: IShsEquipmentData[];
  soldToItems: {
    isInactive: boolean;
    flNumber: string;
    soldToId: string;
    soldToAddress: string;
  }[] = [];

  @Input() isCartOperationInProgress!: boolean;
  @Input() loadingCartDataInProgress!: boolean;
  @Input() loading!: boolean;
  @Input() agreements!: IShsEquipmentData[];
  @Input() searchValue!: string;
  @Input() filters!: AdvancedFilter[];
  @Input() userCarts: Array<ICart> = [];
  @Input() currentCartId!: string;
  @Input() cartItems: any[] = [];
  @Input() productSku: string = '';
  @Input() needsAttention: boolean = false;
  @Input() soldToId!: number;
  @Input() modalitiesAndMaterialNumbers!: IModalitiesAndMaterialNumbers[];
  @Input() isEquipment: boolean = false;
  @Input() selectableEquipment: boolean = false;
  @Input() isReorderPending: boolean = false;
  @Input() companyRoles!: EUserRoles[];
  @Input() isConsumableEquipmentPurchaseEnabled: boolean = false;

  @ViewChildren(SortByComponent)
  sortByComponent!: QueryList<SortByComponent>;

  private unsubscribe$: Subject<void> = new Subject<void>();

  ngOnInit(): void {
    if (this.isEquipment) {
      this.headers.pop();
      this.headers.push(this.myEquipmentAdditionalHeader);
    }

    if (this.soldToId) {
      this.checkSoldToId();
    }
  }

  ngOnChanges(_changes: SimpleChanges): void {
    if (this.soldToId) {
      this.agreementsToWorkWith = this.filterAgreements();
    } else {
      this.agreementsToWorkWith = this.agreements;
    }
  }

  toggleTab(id: string): void {
    const exists: number = this.tabsOpened.findIndex((tab) => tab === id);
    if (exists !== -1) {
      this.tabsOpened.splice(exists, 1);
    } else {
      this.tabsOpened.push(id);
    }
  }

  getAttribute(agreement: IShsEquipmentData, attribute: string, nestedAttribute?: string): string {
    if (attribute === 'contractExpirationDate') {
      const daysTillEndOfContract: number = DateUtils.countDaysFromToday(agreement.attributes.contractExpirationDate ?? '');
      if (daysTillEndOfContract < 0) {
        return '-';
      }
      const contractExpirationDate: string = EquipmentUtils.getAttribute(agreement, attribute, nestedAttribute);
      return DateUtils.isValidDate(contractExpirationDate) ? this.formatDatePipe.transform(contractExpirationDate) ?? '-' : '-';
    }
    return EquipmentUtils.getAttribute(agreement, attribute, nestedAttribute);
  }

  sort(option: any): void {
    this.agreementsToWorkWith = ArrayUtils.sortByAttribute(
      [...this.agreementsToWorkWith],
      `attributes.${option.value}`,
      option.reversed,
      option?.time,
      option?.numeric,
    );

    this.sortByComponent.forEach(component => component.active = component.isOpen ? option : '');
  }

  filterAgreements(): IShsEquipmentData[] {
    return this.agreements.reduce<IShsEquipmentData[]>((acc, next) => {
      const isTheSameFL: boolean = !!this.cartItems.find(item =>
        item.attributes.systemDetails?.siemensEquipmentId === next.attributes?.siemensEquipmentId);
      if (!isTheSameFL) {
        acc.push(next);
      }
      return acc;
    }, []);
  }

  isSoldToItemInArray(siemensEquipmentId: string): boolean {
    return this.soldToId ? this.soldToItems.find(item => item.flNumber === siemensEquipmentId)?.isInactive ?? false : false;
  }

  getAdditionalData(siemensEquipmentId: string): any {
    if (this.soldToId) {
      const data: {isInactive: boolean; flNumber: string; soldToId: string; soldToAddress: string} | undefined = this.soldToItems.find(item => item.flNumber === siemensEquipmentId);
      return {
        soldToId: data?.soldToId,
        soldToAddress: data?.soldToAddress,
      };
    }
    return {soldToId: null, soldToAddress: ''};
  }

  checkSoldToId(): void {
    this.loading = true;
    forkJoin(
      this.agreementsToWorkWith.map(agreement => this.catalogFacade.getShsEquipmentInstalledBaseWithSoldTo(agreement.attributes.siemensEquipmentId)),
    ).pipe(takeUntil(this.unsubscribe$)).subscribe({
      next: ([...args]) => {
        const includes: Array<any> = args.map(arg => arg.included?.[0]).filter(Boolean);
        this.soldToItems = includes.map(include => {
          return ({
            flNumber: include.id,
            isInactive: +include.attributes.customerSoldToId !== this.soldToId,
            soldToId: include.attributes.customerSoldToId,
            soldToAddress: include.attributes.customerSoldToName,
          });
        });
        this.loading = false;
      },
      error: () => {
        this.loading = false;
      },
    });
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }
}
